import { ButtonVariant } from 'component/button/buttonProps';
import { CircularProgress } from '@material-ui/core';
import { flc } from 'utils/strings/firstLetterCapitalize';
import { getNavigationNameForPageEnum, getSubtitleForPageEnum, getTitleForPageEnum, PageEnum } from 'route/page';
import { getRatControlServicesUrl } from 'api/url/apiBackEndUrl';
import { parseRatControlServiceList } from 'model/ratControl/parser/parseRatControlService';
import { ratControlAddressRoute, ratControlConfirmRoute } from 'route/routeRatControl';
import { RatControlService } from 'model/ratControl/ratControlServiceSchema';
import { ServiceType } from 'common/serviceType';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import axios from 'system/axios/axios';
import PageWrapper from 'component/pageWrapper';
import RatControlForm, { RatControlFormValues } from 'component/form/ratControlForm';
import React from 'react';
import ServerError from 'component/error/serverError';
import usePostalCode from 'store/order/hook/address/usePostalCode/usePostalCode';
import useRatControlData from 'store/order/hook/ratControlService/useRatControlData/useRatControlData';
import useSetRatControlDataWasSetAction from 'store/order/hook/ratControlService/useSetRatControlDataWasSetAction/useSetRatControlDataWasSetAction';

const RatControlServicePage: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const postalCode = usePostalCode();
    const ratControlData = useRatControlData();
    const setRatControlDataWasSet = useSetRatControlDataWasSetAction();

    if (postalCode === undefined) {
        history.push(ratControlAddressRoute.url());
        return null;
    }

    const { data: ratControlService, isError } = useQuery<RatControlService>(
        ['ratControlServices', postalCode?.id],
        async () => {
            const postalCodeId = postalCode?.id;
            if (postalCodeId === undefined) {
                throw new Error('Unexpected: postalCodeId is undefined');
            }
            const response = await axios.get(getRatControlServicesUrl(postalCodeId));
            // console.log('response', response);
            const responseData = response.data;
            const ratControlServices = parseRatControlServiceList(responseData.data);

            return ratControlServices[0];
        },
        {
            enabled: postalCode !== undefined,
        },
    );

    if (isError) {
        return <ServerError />;
    }

    const onSubmit = async (inputs: RatControlFormValues) => {
        if (ratControlService === undefined) {
            throw new Error('ratControlData is undefined');
        }
        await setRatControlDataWasSet({
            tariffConfigId: ratControlService.tariffConfigId,
            price: ratControlService.price,
            images: inputs.images,
            problem: inputs.problem,
            description: inputs.description,
        });
        history.push(ratControlConfirmRoute.url());
    };

    let content: React.ReactNode = null;
    if (ratControlService === undefined) {
        content = <CircularProgress />;
    } else {
        content = <RatControlForm id="ratControl" onSubmit={onSubmit} defaultValues={ratControlData} />;
    }

    return (
        <PageWrapper
            page={PageEnum.ratControlService}
            pageTitle={getNavigationNameForPageEnum(PageEnum.ratControlService)}
            serviceType={ServiceType.ratControl}
            wizardItems={[PageEnum.ratControlAddress, PageEnum.ratControlService, PageEnum.ratControlConfirm, PageEnum.ratControlPayment]}
            title={flc(getTitleForPageEnum(PageEnum.ratControlService))}
            subtitle={getSubtitleForPageEnum(PageEnum.ratControlService)}
            price={{
                label: flc(t('order.price')),
                amount: ratControlService?.price ?? ratControlData?.price ?? null,
            }}
            secondButton={{
                type: 'submit',
                form: 'ratControl',
                arrowContinue: true,
                text: t('order.navigation.continue'),
                variant: ButtonVariant.contained,
            }}
            firstButton={{
                onClick: () => {
                    history.push(ratControlAddressRoute.url());
                },
                text: t('order.navigation.back'),
                variant: ButtonVariant.outlined,
            }}
        >
            {content}
        </PageWrapper>
    );
};

export default RatControlServicePage;
